import { useEffect, useMemo, useState } from 'react'

import { FormattedMessage, useIntl } from 'react-intl'
import { LuChevronFirst, LuChevronLast, LuChevronLeft, LuChevronRight } from 'react-icons/lu'

import { SortInfo } from '@/lib/core/interfaces/common.types'
import {
  Button,
  Navigation,
  Spacer,
  Wrapper,
} from '@/lib/core/components/Pagination/Pagination.styles'
import { SelectDefault, SelectOption } from '@/lib/core/components/Select'
import { getPaginationTheme } from '@/lib/core/components/Select/Select.styles'
import messages from '@/lib/core/components/Pagination/Pagination.messages'

export type PaginationPayload = {
  page: number
  pageSize: number
  sortingInfo?: SortInfo
}

export type PaginationProps = {
  initialPage?: number
  initialPageSize?: number
  isLoading: boolean
  onChange: (payload: PaginationPayload) => void
  options?: Array<SelectOption<number>>
  recordsCount: number
  dataTestId?: string
}

export const defaultOptions: Array<SelectOption<number>> = [
  {
    label: '10',
    value: 10,
  },
  {
    label: '20',
    value: 20,
  },
  {
    label: '50',
    value: 50,
  },
  {
    label: '75',
    value: 75,
  },
  {
    label: '100',
    value: 100,
  },
]

export const Pagination = ({
  initialPage = 1,
  initialPageSize = 20,
  isLoading,
  options = defaultOptions,
  onChange,
  recordsCount,
  dataTestId,
}: PaginationProps) => {
  const { formatMessage } = useIntl()
  const defaultOptionIndex = defaultOptions.findIndex(({ value }) => value === initialPageSize)
  const [page, setPage] = useState(initialPage)
  const [pageSize, setPageSize] = useState(initialPageSize)
  const to = useMemo(() => Math.min(page * pageSize, recordsCount), [page, recordsCount, pageSize])
  const from = useMemo(() => (page - 1) * pageSize + 1, [page, pageSize])
  const prevPage = useMemo(() => Math.max(1, page - 1), [page])
  const nextPage = useMemo(
    () => Math.min(page + 1, Math.ceil(recordsCount / pageSize)),
    [page, pageSize, recordsCount],
  )
  const lastPage = useMemo(() => Math.ceil(recordsCount / pageSize), [pageSize, recordsCount])

  const onRowsChange = (option: SelectOption<number> | null) => {
    setPage(1)
    setPageSize(option?.value || options[defaultOptionIndex].value)
  }
  const changePage = (newPage: number) => {
    if (page !== newPage) {
      setPage(newPage)
    }
  }

  useEffect(() => {
    onChange({
      page,
      pageSize,
    })
  }, [page, pageSize])

  useEffect(() => {
    changePage(initialPage)
  }, [initialPage])

  return (
    <Wrapper data-testid={dataTestId}>
      <FormattedMessage {...messages.rowsPerPage} tagName="span" />
      <SelectDefault
        aria-label={formatMessage(messages.rowsPerPage)}
        defaultValue={options[defaultOptionIndex]}
        isDisabled={isLoading}
        isMulti={false}
        isSearchable={false}
        menuPlacement="top"
        // @ts-expect-error This is working as expected
        onChange={onRowsChange}
        options={options}
        selectTheme={(theme) => getPaginationTheme(theme, { menuPlacement: 'top' })}
      />
      <FormattedMessage
        {...messages.paginationState}
        tagName="span"
        values={{
          from,
          recordsCount,
          to,
        }}
      />
      <Navigation>
        <Button isDisabled={page === 1 || isLoading} onClick={() => changePage(1)}>
          <LuChevronFirst size="24" strokeWidth={1.5} />
        </Button>
        <Button isDisabled={page === prevPage || isLoading} onClick={() => changePage(prevPage)}>
          <LuChevronLeft size="24" strokeWidth={1.5} />
        </Button>
        <Spacer />
        <Button isDisabled={page === nextPage || isLoading} onClick={() => changePage(nextPage)}>
          <LuChevronRight size="24" strokeWidth={1.5} />
        </Button>
        <Button isDisabled={page === lastPage || isLoading} onClick={() => changePage(lastPage)}>
          <LuChevronLast size="24" strokeWidth={1.5} />
        </Button>
      </Navigation>
    </Wrapper>
  )
}
